import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, X, ArrowRight, ExternalLink, PartyPopper } from 'lucide-react'
import { onboardingAPI } from '../services/api'
import { useAdminBusiness } from '../contexts/AdminBusinessContext'
import { t } from '../i18n'

const TITLES = {
  restaurant: 'Set up your restaurant',
  ecommerce: 'Set up your online store',
  booking: 'Set up your booking page',
}

// Wording per step; some steps read differently for a shop and a restaurant.
function stepText(key, type) {
  const shop = type === 'ecommerce'
  switch (key) {
    case 'logo': return ['Add your logo', 'It shows on your website, receipts and QR codes.', 'Upload logo']
    case 'category': return shop
      ? ['Create your first category', 'Group your products, e.g. Clothes, Shoes, Accessories.', 'Add category']
      : ['Create your first menu category', 'Group your dishes, e.g. Starters, Mains, Drinks.', 'Add category']
    case 'product': return shop
      ? ['Add your first product', 'Give it a name, a price, photos and stock.', 'Add product']
      : ['Add your first dish', 'Give it a name, a price and a photo.', 'Add dish']
    case 'tables': return ['Add your tables', 'Each table gets a QR code customers scan to order.', 'Add tables']
    case 'payments': return ['Choose how customers pay', 'Cash, card with Stripe, or bank transfer.', 'Set up payments']
    case 'service': return ['Add your first service', 'Its name, price and how long it takes.', 'Add service']
    case 'hours': return ['Set your opening hours', 'Customers can only book when you are open.', 'Set hours']
    case 'first_booking': return ['Share your booking page', 'Open your page, try a booking, then send the link to your customers.', 'Open website']
    default: return ['Share your website and get your first order', 'Open your website, try an order, then send the link to your customers.', 'Open website']
  }
}

export default function OnboardingChecklist() {
  const navigate = useNavigate()
  const { branch } = useAdminBusiness()
  const [guide, setGuide] = useState(null)

  const load = useCallback(() => {
    onboardingAPI.get().then(r => setGuide(r.data)).catch(() => setGuide(null))
  }, [])
  useEffect(() => {
    load()
    window.addEventListener('focus', load)
    return () => window.removeEventListener('focus', load)
  }, [load])

  const dismiss = () => {
    setGuide(g => ({ ...g, dismissed: true }))
    onboardingAPI.dismiss().catch(() => {})
  }

  if (!guide || guide.dismissed) return null

  const steps = guide.steps
  const doneCount = steps.filter(s => s.done).length
  const nextKey = steps.find(s => !s.done)?.key
  const kind = guide.businessType === 'ecommerce' ? 'ecommerce' : guide.businessType === 'restaurant' ? 'restaurant' : 'booking'

  const open = step => {
    if (step.href === 'website') {
      const url = branch?.website_url || `/?branch=${branch?.id || ''}`
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      navigate(step.href)
    }
  }

  if (guide.completed) {
    return (
      <div className="mb-6 flex items-center gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:p-5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
          <PartyPopper className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-emerald-900">{t("You're all set!")}</p>
          <p className="text-sm text-emerald-800">{t('Your business is ready for customers.')}</p>
        </div>
        <button onClick={dismiss} className="rounded-lg p-2 text-emerald-700 hover:bg-emerald-100" title={t('Hide')}>
          <X className="h-5 w-5" />
        </button>
      </div>
    )
  }

  return (
    <section className="mb-6 rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-start gap-3 border-b border-gray-100 p-4 sm:p-5">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-gray-900">{t(TITLES[kind])}</h2>
          <p className="mt-0.5 text-sm text-gray-500">{t('{done} of {total} steps done', { done: doneCount, total: steps.length })}</p>
          <div className="mt-3 h-2 w-full max-w-sm overflow-hidden rounded-full bg-gray-100">
            <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${(doneCount / steps.length) * 100}%` }} />
          </div>
        </div>
        <button onClick={dismiss} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600" title={t('Hide the setup guide')}>
          <X className="h-5 w-5" />
        </button>
      </div>

      <ol className="divide-y divide-gray-100">
        {steps.map((step, i) => {
          const [title, description, action] = stepText(step.key, guide.businessType)
          const isNext = step.key === nextKey
          return (
            <li key={step.key} className={`flex items-center gap-3 px-4 py-3 sm:px-5 ${isNext ? 'bg-blue-50/60' : ''}`}>
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                step.done ? 'bg-emerald-500 text-white' : isNext ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {step.done ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-medium ${step.done ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{t(title)}</p>
                {!step.done && <p className="text-xs text-gray-500 sm:text-sm">{t(description)}</p>}
              </div>
              {!step.done && (
                <button
                  onClick={() => open(step)}
                  aria-label={t(action)}
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium ${
                    isNext ? 'bg-blue-600 text-white hover:bg-blue-700' : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="hidden sm:inline">{t(action)}</span>
                  {step.href === 'website' ? <ExternalLink className="h-4 w-4" /> : <ArrowRight className="h-4 w-4 rtl:rotate-180" />}
                </button>
              )}
            </li>
          )
        })}
      </ol>
    </section>
  )
}
